import { PermissionKey } from 'src/types/permissions.types';
import {
  canAccessRouteSection,
  getRoleBaseRoute,
  hasRolePermission,
  isKnownRole,
  resolveViewScope,
} from './permissions';

export type RouteAccessDecision =
  | { status: 'loading' }
  | { status: 'allow' }
  | { status: 'redirect'; to: string };

export interface RouteAccessInput {
  isLoading: boolean;
  isAuthenticated: boolean;
  role?: string;
  /** Top-level section being entered, for example '/admin'. */
  section?: string;
  requiredPermission?: PermissionKey | string;
  /** Resource the route reads. Any scope above NONE passes. */
  requiredViewResource?: string;
  sessionExpired?: boolean;
}

function loginRoute(sessionExpired?: boolean): string {
  return sessionExpired ? '/login?session_expired=true' : '/login';
}

/** The landing page for a role, derived from its base route. */
export function homeRoute(role?: string): string {
  return getRoleBaseRoute(role, '/dashboard');
}

function isSectionDenied(role: string | undefined, section?: string): boolean {
  if (!section) return false;
  return !canAccessRouteSection(role, section);
}

function isPermissionDenied(role: string | undefined, permission?: PermissionKey | string): boolean {
  if (!permission) return false;
  return !hasRolePermission(role, permission);
}

function isViewDenied(role: string | undefined, resource?: string): boolean {
  if (!resource) return false;
  return resolveViewScope(role, resource) === 'NONE';
}

/** Where an authorised-but-denied user should be sent, or null when allowed. */
function deniedRedirect(input: RouteAccessInput): string | null {
  if (isSectionDenied(input.role, input.section)) return homeRoute(input.role);
  if (isPermissionDenied(input.role, input.requiredPermission)) return homeRoute(input.role);
  if (isViewDenied(input.role, input.requiredViewResource)) return homeRoute(input.role);
  return null;
}

/**
 * Decides whether a user may enter a route. Section is checked before
 * permission, so a denied user lands on their own dashboard, not a 404.
 */
export function resolveRouteAccess(input: RouteAccessInput): RouteAccessDecision {
  if (input.isLoading) return { status: 'loading' };
  if (!input.isAuthenticated) return { status: 'redirect', to: loginRoute(input.sessionExpired) };
  // No role yet means the profile fetch is still in flight.
  if (!input.role) return { status: 'loading' };
  // An unrecognised role is a misconfiguration, not a lesser privilege.
  if (!isKnownRole(input.role)) return { status: 'redirect', to: loginRoute(input.sessionExpired) };

  const denied = deniedRedirect(input);
  if (denied) return { status: 'redirect', to: denied };
  return { status: 'allow' };
}
